import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, field, StringDecoder } from '@common/encoding';
import { Sorter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { GroupPriceDiscount } from './GroupPriceDiscount.js';
import { calculateHungarianAlgorithm } from './helpers/calculateHungarianAlgorithm.js';
import { RegisterItem } from './members/checkout/RegisterItem.js';
import { RegistrationWithPlatformMember } from './members/checkout/RegistrationWithPlatformMember.js';
import { TranslatedString } from './TranslatedString.js';
export class BundleDiscount extends AutoEncoder {
    id;
    name = new TranslatedString('');
    discounts = []; // second, third, fourth...
    /**
     * Whether all registrations of the family are counted together (true), or they are all counted separately per member (false).
     */
    countWholeFamily = false;
    /**
     * Whether all registrations are counted together regardless the group (false), or each group is counted separately (true).
     * Note: this is always false (ignored) if countWholeFamily is false. Because a member cannot register for the same group multiple times.
     */
    countPerGroup = false;
    get humanDescription() {
        if (this.countWholeFamily) {
            if (this.countPerGroup) {
                return $t('%178');
            }
            return $t('%179');
        }
        return $t('%17A');
    }
    static discountsToText(discounts) {
        if (discounts.length === 0) {
            return $t('%17B');
        }
        return discounts.map((d, i) => {
            if (i === discounts.length - 1) {
                return $t('%17C', {
                    '5%': d.toString(),
                    '2': i + 2,
                });
            }
            return $t('%17D', {
                '5%': d.toString(),
                '2': i + 2,
            });
        }).join(', ');
    }
    get discountsText() {
        return BundleDiscount.discountsToText(this.discounts);
    }
    calculate(cart, periodId) {
        const group = new BundleDiscountCalculationGroup({
            bundle: this,
            periodId,
        });
        for (const item of cart.items) {
            const calculation = group.getCalculationFor(item);
            calculation?.add(item);
            for (const deletedRegistration of item.replaceRegistrations) {
                const cc = group.getCalculationFor(deletedRegistration);
                cc?.remove(deletedRegistration);
            }
        }
        for (const { registration: deletedRegistration } of cart.deleteRegistrations) {
            const cc = group.getCalculationFor(deletedRegistration);
            cc?.remove(deletedRegistration);
        }
        // Loop all existing registrations that are not deleted - if they are already in a calculation group
        const loopedFamilies = new Set();
        const loopedMembers = new Set();
        for (const family of [
            ...cart.items.map(item => item.member.family),
            ...cart.deleteRegistrations.map(item => item.registration.member.family),
        ]) {
            if (loopedFamilies.has(family)) {
                continue;
            }
            loopedFamilies.add(family);
            for (const member of family.members) {
                if (loopedMembers.has(member.id)) {
                    continue;
                }
                loopedMembers.add(member.id);
                for (const registration of member.filterRegistrations({ periodId: periodId })) {
                    const registrationWithPlatformMember = new RegistrationWithPlatformMember({
                        registration,
                        member,
                    });
                    const cc = group.getCalculationFor(registrationWithPlatformMember, {
                        // Only add it to the calculation if this registration is relevant for a new or removed registration that was added earlier
                        onlyExisting: true,
                    });
                    cc?.addIfNotBeingRemoved(registrationWithPlatformMember);
                }
            }
        }
        group.calculate();
        return group;
    }
    applyableTo(item, periodId) {
        // A bundle discount only applies to registrations of the period it belongs to.
        // Historically, duplicating a period re-used the same discount id across periods,
        // so we have to filter on the period to avoid mixing registrations from other periods.
        if (item.group.periodId !== periodId) {
            return false;
        }
        const groupPrice = item instanceof RegistrationWithPlatformMember ? item.registration.updatedGroupPrice : item.groupPrice;
        if (groupPrice.bundleDiscounts.has(this.id)) {
            return true;
        }
        if (item instanceof RegistrationWithPlatformMember) {
            if (item.registration.discounts.has(this.id)) {
                // This registration received the discount in the past, so it is still applyable
                return true;
            }
        }
        return false;
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], BundleDiscount.prototype, "id", void 0);
__decorate([
    field(TranslatedString.field({}))
], BundleDiscount.prototype, "name", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(GroupPriceDiscount) })
], BundleDiscount.prototype, "discounts", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], BundleDiscount.prototype, "countWholeFamily", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], BundleDiscount.prototype, "countPerGroup", void 0);
class BundleDiscountCalculationGroup {
    bundle;
    periodId;
    calculations = new Map();
    constructor(options) {
        this.bundle = options.bundle;
        this.periodId = options.periodId;
    }
    calculate() {
        for (const calculation of this.calculations.values()) {
            calculation.calculate();
        }
    }
    getCalculationFor(item, options) {
        if (!this.bundle.applyableTo(item, this.periodId)) {
            return null;
        }
        let groupingCode = 'family-' + item.member.family.uuid + '-';
        let group = null;
        let member = null;
        if (this.bundle.countWholeFamily) {
            if (this.bundle.countPerGroup) {
                groupingCode += item.group.id;
                group = item.group;
            }
            else {
                // Group by family objects (for admins)
            }
        }
        else {
            member = item.member;
            groupingCode += item.member.id;
        }
        const existing = this.calculations.get(groupingCode);
        if (!existing) {
            if (options?.onlyExisting) {
                return null;
            }
            const n = new BundleDiscountCalculation({
                bundle: this.bundle,
                group,
                member,
            });
            this.calculations.set(groupingCode, n);
            return n;
        }
        return existing;
    }
}
export class BundleDiscountCalculation {
    bundle;
    member; // Only set when it is scoped to single members, not a family
    group; // Only set when scoped to a single group, not multiple groups
    /**
     *  Keep track of the distribution of discounts.
     *  This will set the 'applied discounts' for these items once their registration is created in the database
     */
    items;
    /**
     *  Existing registations that cause a discount.
     *  The value contains the new calculated discount for this registration
     */
    registrations;
    /**
     * Keeps track of the deleted registrations so we can exclude these registrations from the calculation
    */
    deleteRegistrations;
    constructor(options) {
        this.bundle = options.bundle;
        this.group = options.group ?? null;
        this.member = options.member ?? null;
        this.items = new Map();
        this.registrations = new Map();
        this.deleteRegistrations = [];
    }
    get name() {
        const suffix = [];
        if (this.group) {
            suffix.push(this.group.settings.name.toString());
        }
        if (this.member) {
            suffix.push(this.member.patchedMember.firstName);
        }
        return this.bundle.name + (suffix.length > 0 ? ' (' + suffix.join(', ') + ')' : '');
    }
    /**
        The total dicount, not taking into account what has already been given in the past
    */
    get total() {
        return [...this.items.values()].reduce((a, b) => a + b, 0)
            + [...this.registrations.values()].reduce((a, b) => a + b, 0);
    }
    get totalAlreadyApplied() {
        let c = 0;
        for (const registration of this.registrations.keys()) {
            c += registration.registration.discounts.get(this.bundle.id)?.amount ?? 0;
        }
        // Note: do not subtract the discounts on the deleted registrations
        // These registrations will be deleted anyway, so their balance will be cancelled, that balance includes their discounts
        // the bundle discounts only reflect the situation without all the deleted registrations
        return c;
    }
    get netTotal() {
        return this.total - this.totalAlreadyApplied;
    }
    get netTotalDueLater() {
        let sum = 0;
        for (const [item, value] of this.items) {
            if (item.calculatedTrialUntil) {
                // Due later
                sum += value;
            }
        }
        return sum;
    }
    get netTotalDueNow() {
        return this.netTotal - this.netTotalDueLater;
    }
    getTotalFor(item) {
        if (item instanceof RegisterItem) {
            return this.items.get(item) ?? 0;
        }
        else if (item instanceof RegistrationWithPlatformMember) {
            return (this.registrations.get(item) ?? 0);
        }
        return 0;
    }
    addIfNotBeingRemoved(item) {
        // Only add if it is not being removed at the same time
        if (item instanceof RegistrationWithPlatformMember) {
            if (this.deleteRegistrations.find(r => r.registration.id === item.registration.id)) {
                return;
            }
        }
        this.add(item);
    }
    add(item) {
        if (item instanceof RegistrationWithPlatformMember) {
            this.registrations.set(item, 0);
        }
        else if (item instanceof RegisterItem) {
            this.items.set(item, 0);
        }
    }
    remove(registration) {
        this.deleteRegistrations.push(registration);
    }
    get itemsAndRegistrations() {
        // Return the items in such an order that the first ones should receive the highest discount
        // The first ones that should receive the highest discount are:
        // - the newest items
        // - registrations or items that edit a registration, in order of the already applied discount
        const itemsOrder = [...this.items.keys()].reverse();
        const registrations = [...this.registrations.keys()];
        const now = new Date();
        const itemsWithSortingData = itemsOrder.map((item) => {
            return {
                item,
                appliedDiscount: item.replaceRegistrations.length >= 1 ? (item.replaceRegistrations[0].registration.discounts.get(this.bundle.id)?.amount ?? 0) : 0,
                createdAt: item.replaceRegistrations.length >= 1 ? (item.replaceRegistrations[0].registration.createdAt) : now,
            };
        });
        const registrationsWithSortingData = registrations.map((registration) => {
            return {
                item: registration,
                appliedDiscount: registration.registration.discounts.get(this.bundle.id)?.amount ?? 0,
                createdAt: registration.registration.createdAt,
            };
        });
        const base = [
            ...itemsWithSortingData,
            ...registrationsWithSortingData,
        ].sort((a, b) => {
            return Sorter.stack(Sorter.byNumberValue(a.appliedDiscount, b.appliedDiscount), Sorter.byDateValue(a.createdAt, b.createdAt));
        });
        return base.map(b => b.item);
    }
    calculate() {
        // For each item and registration, calculate the price matrix
        // Price if used as first item, second item, third item
        // We'll then calculate the optimal order using the Hungarian Algorithm
        const arr = this.itemsAndRegistrations;
        const priceMatrix = [];
        for (const item of arr) {
            // We always use the group price here, because this is safer, also for registrations (in case we have deactivated registrations with cancallation fees)
            const price = item instanceof RegisterItem ? (item.groupPrice.price.forMember(item.member)) : item.registration.groupPrice.price.forMember(item.member);
            const discounts = item instanceof RegisterItem
                ? (item.groupPrice.bundleDiscounts.get(this.bundle.id)?.customDiscounts ?? this.bundle.discounts)
                : (item.registration.groupPrice.bundleDiscounts.get(this.bundle.id)?.customDiscounts ?? this.bundle.discounts);
            const row = [];
            for (const [index] of arr.entries()) {
                if (index === 0) {
                    row.push(0);
                    continue;
                }
                const discount = discounts[index - 1] ?? discounts[discounts.length - 1]; // Repeat last
                const calculated = discount.calculateDiscount(price, item.member);
                row.push(calculated);
            }
            priceMatrix.push(row);
        }
        const solved = calculateHungarianAlgorithm(priceMatrix);
        for (const [index, item] of arr.entries()) {
            const nth = solved[index];
            const discount = priceMatrix[index][nth];
            if (item instanceof RegistrationWithPlatformMember) {
                this.registrations.set(item, discount);
            }
            else {
                this.items.set(item, discount);
            }
        }
    }
}
//# sourceMappingURL=BundleDiscount.js.map